"use client";

import React, { useEffect } from "react";

import { useState } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { Star } from "lucide-react";
import PropTypes from "prop-types";
import "./form-renderer.css";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";

export function FormRenderer({ form, onSubmit }) {
  console.log("Form:", form);
  const [errors, setErrors] = useState({});
  const [rating, setRating] = useState({});
  const [submissionData, setSubmissionData] = useState(null);

  const [formData, setFormData] = useState(() => {
    const initialData = {};
    form.fields?.forEach((field) => {
      initialData[field.id] = "";
    });
    return initialData;
  });

  // useEffect(() => {
  //   if (submissionData) {
  //     setFormData((prev) => ({ ...prev, ...submissionData }));
  //   }
  // }, [submissionData]);

  const me = async () => {
    const response = await axios({
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/auth/me`,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
    // console.log("🚀 ~ fetchUsers ~ response:", response);
    return response.data;
  };

  const {
    data: meData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["me"], // Unique key for the query
    queryFn: me, // The async function to run
    retry: false,
  });

  useEffect(() => {
    if (!form?.id || !meData?.user_id) return;

    const fetchSubmission = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/allforms/${form.id}/${meData.user_id}`,
          { withCredentials: true }
        );

        console.log("Fetched Submission Data:", res.data);

        if (res.data?.data?.submission) {
          setSubmissionData(res.data.data.submission);
        } else {
          setSubmissionData(null); // reset when no submission
        }
      } catch (err) {
        console.error("Error fetching submission:", err);
      }
    };

    fetchSubmission();
  }, [form?.id, meData?.user_id]);

  const renderPreviewField = (field) => {
    return (
      <div key={field.id} className="form-field">
        <Label className="form-label">{field.label}</Label>
        <Input
          value={submissionData?.[field.id] || ""}
          readOnly
          disabled
          className="form-input bg-gray-100 cursor-not-allowed"
        />
      </div>
    );
  };

  const validateField = (field, value) => {
    if (field.required && (!value || (Array.isArray(value) && value.length === 0))) {
      return `${field.label} is required`;
    }

    if (field.validation) {
      if (field.validation.minLength && value && value.length < field.validation.minLength) {
        return `${field.label} must be at least ${field.validation.minLength} characters`;
      }
      if (field.validation.maxLength && value && value.length > field.validation.maxLength) {
        return `${field.label} must be no more than ${field.validation.maxLength} characters`;
      }
    }

    if (field.type === "email" && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return "Please enter a valid email address";
    }

    if (
      field.type === "phone" &&
      value &&
      !/^[+]?[1-9][\d]{0,15}$/.test(value.replace(/\s/g, ""))
    ) {
      return "Please enter a valid phone number";
    }

    return null;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const newErrors = {};

    form.fields.forEach((field) => {
      const value = field.type === "rating" ? rating[field.id] : formData[field.id];
      const error = validateField(field, value);
      if (error) {
        newErrors[field.id] = error;
      }
    });

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      const submissionData = {
        ...formData,
        ...rating,
        submittedAt: new Date().toISOString(),
      };
      onSubmit(submissionData);
    }
  };

  const updateFormData = (fieldId, value) => {
    setFormData((prev) => ({ ...prev, [fieldId]: value }));
    if (errors[fieldId]) {
      setErrors((prev) => ({ ...prev, [fieldId]: "" }));
    }
  };

  const renderField = (field) => {
    const error = errors[field.id];

    switch (field.type) {
      case "textarea":
        return (
          <div key={field.id} className="form-field">
            <Label htmlFor={field.id} className="form-label">
              {field.label}
              {field.required && (
                <Badge variant="destructive" className="form-required">
                  Required
                </Badge>
              )}
            </Label>
            <Textarea
              id={field.id}
              placeholder={field.placeholder}
              value={formData[field.id] || ""}
              onChange={(e) => updateFormData(field.id, e.target.value)}
              className={`form-input ${error ? "border-destructive" : ""}`}
            />
            {error && <p className="form-error">{error}</p>}
          </div>
        );

      case "select":
        return (
          <div key={field.id} className="form-field">
            <Label htmlFor={field.id} className="form-label">
              {field.label}
              {field.required && (
                <Badge variant="destructive" className="form-required">
                  Required
                </Badge>
              )}
            </Label>
            <Select
              value={formData[field.id] || ""}
              onValueChange={(value) => updateFormData(field.id, value)}
            >
              <SelectTrigger className={`form-select ${error ? "border-destructive" : ""}`}>
                <SelectValue placeholder={field.placeholder || "Select an option"} />
              </SelectTrigger>
              <SelectContent className="form-select-dropdown">
                {field.options?.map((option, index) => (
                  <SelectItem key={index} value={option} className="form-select-item">
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {error && <p className="form-error">{error}</p>}
          </div>
        );

      case "radio":
        return (
          <div key={field.id} className="form-field">
            <Label className="form-label">
              {field.label}
              {field.required && (
                <Badge variant="destructive" className="form-required">
                  Required
                </Badge>
              )}
            </Label>
            <RadioGroup
              value={formData[field.id] || ""}
              onValueChange={(value) => updateFormData(field.id, value)}
              className={`form-radio-group ${error ? "error" : ""}`}
            >
              {field.options?.map((option, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "6px", // spacing between each option
                    minHeight: "28px", // makes row slightly taller
                  }}
                >
                  <RadioGroupItem value={option} id={`${field.id}-${index}`} />
                  <Label htmlFor={`${field.id}-${index}`} className="form-radio-item">
                    {option}
                  </Label>
                </div>
              ))}
            </RadioGroup>
            {error && <p className="form-error">{error}</p>}
          </div>
        );

      case "checkbox":
        return (
          <div key={field.id} className="form-field">
            <Label className="form-label">
              {field.label}
              {field.required && (
                <Badge variant="destructive" className="form-required">
                  Required
                </Badge>
              )}
            </Label>
            <div className={`form-radio-group ${error ? "error" : ""}`}>
              {field.options?.map((option, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "6px", // spacing between each option
                    minHeight: "28px", // makes row slightly taller
                  }}
                >
                  <Checkbox
                    id={`${field.id}-${index}`}
                    checked={(formData[field.id] || []).includes(option)}
                    onCheckedChange={(checked) => {
                      const currentValues = formData[field.id] || [];
                      if (checked) {
                        updateFormData(field.id, [...currentValues, option]);
                      } else {
                        updateFormData(
                          field.id,
                          currentValues.filter((v) => v !== option)
                        );
                      }
                    }}
                  />
                  <Label htmlFor={`${field.id}-${index}`} className="form-radio-item">
                    {option}
                  </Label>
                </div>
              ))}
            </div>
            {error && <p className="form-error">{error}</p>}
          </div>
        );

      case "rating":
        return (
          <div key={field.id} className="form-field">
            <Label className="form-label">
              {field.label}
              {field.required && (
                <Badge variant="destructive" className="form-required">
                  Required
                </Badge>
              )}
            </Label>
            <div className={`form-rating ${error ? "form-rating-error" : ""}`}>
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating((prev) => ({ ...prev, [field.id]: star }))}
                  className={`form-rating-star ${star <= (rating[field.id] || 0) ? "active" : ""}`}
                >
                  <Star />
                </button>
              ))}
            </div>
            {error && <p className="form-error">{error}</p>}
          </div>
        );

      default:
        return (
          <div key={field.id} className="form-field">
            <Label htmlFor={field.id} className="form-label">
              {field.label}
              {field.required && (
                <Badge variant="destructive" className="form-required">
                  Required
                </Badge>
              )}
            </Label>
            <Input
              id={field.id}
              type={field.type}
              placeholder={field.placeholder}
              value={formData[field.id] || ""}
              onChange={(e) => updateFormData(field.id, e.target.value)}
              className={`form-input ${error ? "border-destructive" : ""}`}
            />
            {error && <p className="form-error">{error}</p>}
          </div>
        );
    }
  };

  return (
    <div className="split-screen">
      {submissionData && Object.keys(submissionData).length > 0 && (
        <Card className="form-renderer-card">
          <CardHeader className="form-renderer-header">
            <CardTitle className="form-renderer-title">Previous Submitted Form Preview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">{form.fields.map(renderPreviewField)}</div>
          </CardContent>
        </Card>
      )}
      <Card className="form-renderer-card">
        <CardHeader className="form-renderer-header">
          <CardTitle className="form-renderer-title">{form.title}</CardTitle>
          {form.description && (
            <CardDescription className="form-renderer-desc">{form.description}</CardDescription>
          )}
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {form.fields.map(renderField)}

            <Button type="submit" className="btn btn-primary form-submit">
              Submit Form
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

FormRenderer.propTypes = {
  form: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    fields: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        required: PropTypes.bool,
        placeholder: PropTypes.string,
        options: PropTypes.arrayOf(PropTypes.string),
        validation: PropTypes.shape({
          minLength: PropTypes.number,
          maxLength: PropTypes.number,
        }),
      })
    ).isRequired,
  }).isRequired,
  onSubmit: PropTypes.func.isRequired,
};
